import React, { useState, useRef } from 'react';
import { differenceInYears } from 'date-fns';
import { Plus, Calculator, Scale, Upload, Download, FileDown, CheckCircle2, AlertTriangle, RotateCcw } from 'lucide-react';
import { saveAs } from 'file-saver';
import { hesaplaZamanAsimi } from '../zamanasimi/zamanAsimiHesaplayici';
import { zmnSerialize, zmnParse, zmnDosyaAdi } from '../zamanasimi/zmnDosya';
import TarihGirisi from '../zamanasimi/TarihGirisi';
import ZamanAsimiSuresiSecimi from '../zamanasimi/ZamanAsimiSuresiSecimi.js';
import DurduranSebepModal from '../zamanasimi/DurduranSebepModal.js';
import KesenSebepModal from '../zamanasimi/KesenSebepModal.js';
import SebepKarti from '../zamanasimi/SebepKarti.js';
import SonucGosterimi from '../zamanasimi/SonucGosterimi.js';
import YasGrubuSecimi from '../zamanasimi/YasGrubuSecimi';
import ZamanAsimiOzet from '../zamanasimi/ZamanAsimiOzet';
import ZamanAsimiTimeline from '../zamanasimi/ZamanAsimiTimeline';
import RaporIndirButonlari from '../zamanasimi/rapor/RaporIndirButonlari';

function SebepBolumu({ baslik, altBaslik, accent, sebepler, bosMetin, onEkle, onDuzenle, onSil }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-800">{baslik}</h3>
          <p className="truncate text-xs text-slate-500">{altBaslik}</p>
        </div>
        <button
          type="button"
          onClick={() => onEkle()}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
        >
          <Plus className="h-3.5 w-3.5" /> Ekle
        </button>
      </div>
      <div className="space-y-2 p-3">
        {sebepler.length === 0 ? (
          <p className="py-5 text-center text-xs text-slate-400">{bosMetin}</p>
        ) : (
          sebepler.map((s, i) => (
            <SebepKarti
              key={i}
              sebep={s}
              accent={accent}
              onDuzenle={() => onDuzenle(i)}
              onSil={() => onSil(i)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function ZamanAsimi() {
  const [sucTarihi, setSucTarihi] = useState(new Date());
  const [zamanAsimiSuresi, setZamanAsimiSuresi] = useState(8);
  const [durduranSebepler, setDurduranSebepler] = useState([]);
  const [kesenSebepler, setKesenSebepler] = useState([]);
  const [sonuc, setSonuc] = useState(null);
  const [dogumTarihi, setDogumTarihi] = useState(null);
  const [manuelYasGrubu, setManuelYasGrubu] = useState(true);
  const [secilenYasGrubu, setSecilenYasGrubu] = useState('yetiskin');
  const [kesenModal, setKesenModal] = useState({ acik: false, index: null });
  const [durduranModal, setDurduranModal] = useState({ acik: false, index: null });
  const [surukleniyor, setSurukleniyor] = useState(false);
  const [mesaj, setMesaj] = useState(null); // { tip: 'ok' | 'err', text }
  const dosyaInputRef = useRef(null);
  const dragSayac = useRef(0);

  const yasGrubunuBelirle = (yas) => (yas < 15 ? '12-15' : yas < 18 ? '15-18' : 'yetiskin');

  // Otomatik (doğum tarihi) modda yaş grubu + geçerlilik denetimi.
  const yasBilgisi = React.useMemo(() => {
    if (manuelYasGrubu || !dogumTarihi || !sucTarihi) return { grup: null, hata: null };
    if (new Date(dogumTarihi) > new Date(sucTarihi)) {
      return { grup: null, hata: 'Doğum tarihi suç tarihinden sonra olamaz.' };
    }
    const yil = differenceInYears(sucTarihi, dogumTarihi);
    if (yil < 12) {
      return { grup: null, hata: 'Suç tarihinde 12 yaşından küçük; ceza sorumluluğu yoktur (TCK 31).' };
    }
    return { grup: yasGrubunuBelirle(yil), hata: null };
  }, [manuelYasGrubu, dogumTarihi, sucTarihi]);

  // Suça sürüklenen çocuk mu? (HAGB denetim süresi eşiği: çocukta 3 yıl, yetişkinde 5 yıl)
  const aktifYasGrubu = manuelYasGrubu ? secilenYasGrubu : yasBilgisi.grup;
  const sucaSuruklenenCocuk = aktifYasGrubu === '12-15' || aktifYasGrubu === '15-18';

  const hesapla = () => {
    if (yasBilgisi.hata) return; // geçersiz yaş — hesaplama engellenir
    let hesaplamaIcinYasGrubu = secilenYasGrubu;
    if (!manuelYasGrubu && dogumTarihi) {
      hesaplamaIcinYasGrubu = yasBilgisi.grup || secilenYasGrubu;
    }
    setSonuc(
      hesaplaZamanAsimi(sucTarihi, zamanAsimiSuresi, durduranSebepler, kesenSebepler, hesaplamaIcinYasGrubu)
    );
  };

  const gosterMesaj = (tip, text) => {
    setMesaj({ tip, text });
    setTimeout(() => setMesaj(null), 2800);
  };

  // Yeni hesap: tüm girdileri ve sonucu varsayılana döndürür.
  const yeniHesap = () => {
    setSucTarihi(new Date());
    setZamanAsimiSuresi(8);
    setDurduranSebepler([]);
    setKesenSebepler([]);
    setDogumTarihi(null);
    setManuelYasGrubu(true);
    setSecilenYasGrubu('yetiskin');
    setKesenModal({ acik: false, index: null });
    setDurduranModal({ acik: false, index: null });
    setSonuc(null);
    gosterMesaj('ok', 'Yeni hesap için form temizlendi.');
  };

  // --- Dışa aktar (.zmn) ---
  const disaAktar = () => {
    const obj = zmnSerialize({
      sucTarihi,
      zamanAsimiSuresi,
      dogumTarihi,
      manuelYasGrubu,
      secilenYasGrubu,
      kesenSebepler,
      durduranSebepler,
    });
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
    saveAs(blob, zmnDosyaAdi({ sucTarihi }));
    gosterMesaj('ok', 'Veriler .zmn dosyasına aktarıldı.');
  };

  // --- İçe aktar: durumu yükle ve sonucu doğrudan hesapla (state async olduğu için) ---
  const verileriYukle = (v) => {
    setSucTarihi(v.sucTarihi);
    setZamanAsimiSuresi(v.zamanAsimiSuresi);
    setDurduranSebepler(v.durduranSebepler);
    setKesenSebepler(v.kesenSebepler);
    setDogumTarihi(v.dogumTarihi);
    setManuelYasGrubu(v.manuelYasGrubu);
    setSecilenYasGrubu(v.secilenYasGrubu);

    let yg = v.secilenYasGrubu;
    if (!v.manuelYasGrubu && v.dogumTarihi) {
      yg = yasGrubunuBelirle(differenceInYears(v.sucTarihi, v.dogumTarihi));
    }
    setSonuc(hesaplaZamanAsimi(v.sucTarihi, v.zamanAsimiSuresi, v.durduranSebepler, v.kesenSebepler, yg));
  };

  const dosyaOku = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        verileriYukle(zmnParse(String(reader.result)));
        gosterMesaj('ok', 'Veriler yüklendi.');
      } catch (e) {
        gosterMesaj('err', 'Dosya okunamadı. Geçerli bir .zmn dosyası seçin.');
      }
    };
    reader.onerror = () => gosterMesaj('err', 'Dosya okunamadı.');
    reader.readAsText(file);
  };

  const dosyaSecildi = (e) => {
    dosyaOku(e.target.files && e.target.files[0]);
    e.target.value = ''; // aynı dosya tekrar seçilebilsin
  };

  // --- Sürükle-bırak (sayfanın herhangi bir yerine .zmn bırakılınca yükler) ---
  const onDragEnter = (e) => {
    e.preventDefault();
    dragSayac.current += 1;
    setSurukleniyor(true);
  };
  const onDragOver = (e) => e.preventDefault();
  const onDragLeave = (e) => {
    e.preventDefault();
    dragSayac.current -= 1;
    if (dragSayac.current <= 0) setSurukleniyor(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    dragSayac.current = 0;
    setSurukleniyor(false);
    dosyaOku(e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  // Kesen sebepler — ekle / düzenle / sil
  const kesenKaydet = (sebep) => {
    setKesenSebepler((prev) =>
      kesenModal.index == null ? [...prev, sebep] : prev.map((s, i) => (i === kesenModal.index ? sebep : s))
    );
    setKesenModal({ acik: false, index: null });
  };
  const silKesen = (index) => setKesenSebepler((prev) => prev.filter((_, i) => i !== index));

  // Durduran sebepler — ekle / düzenle / sil
  const durduranKaydet = (sebep) => {
    setDurduranSebepler((prev) =>
      durduranModal.index == null
        ? [...prev, sebep]
        : prev.map((s, i) => (i === durduranModal.index ? sebep : s))
    );
    setDurduranModal({ acik: false, index: null });
  };
  const silDurduran = (index) => setDurduranSebepler((prev) => prev.filter((_, i) => i !== index));

  return (
    <div
      className="relative min-h-screen bg-slate-50"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <input
        ref={dosyaInputRef}
        type="file"
        accept=".zmn,application/json"
        className="hidden"
        onChange={dosyaSecildi}
      />

      {/* Sürükle-bırak katmanı */}
      {surukleniyor ? (
        <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-indigo-900/30 p-8 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-indigo-400 bg-white/90 px-10 py-8 shadow-xl">
            <FileDown className="h-8 w-8 text-indigo-600" />
            <p className="text-sm font-semibold text-slate-800">.zmn dosyasını buraya bırakın</p>
            <p className="text-xs text-slate-500">Kayıtlı veriler otomatik yüklenecek</p>
          </div>
        </div>
      ) : null}

      {/* Bilgi balonu (toast) */}
      {mesaj ? (
        <div
          className={`fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium shadow-lg ${
            mesaj.tip === 'ok' ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'
          }`}
        >
          {mesaj.tip === 'ok' ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          {mesaj.text}
        </div>
      ) : null}

      <div className="mx-auto max-w-5xl px-4 py-8 sm:py-10">
        {/* Başlık */}
        <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-indigo-600/10 p-2.5 text-indigo-600">
              <Scale className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                Dava Zamanaşımı Hesaplama{' '}
                <span className="align-middle text-lg font-semibold text-amber-600">(Deneysel)</span>
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                TCK 66–67 uyarınca dava zamanaşımı süresini hesaplayın ve zaman çizelgesinde görselleştirin.
              </p>
            </div>
          </div>

          {/* Yeni hesap (vurgulu) · İçe / Dışa aktar (nötr) */}
          <div className="flex shrink-0 flex-wrap items-center gap-2 self-start">
            <button
              type="button"
              onClick={yeniHesap}
              title="Formu temizle, yeni hesaba başla"
              className="group inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2 text-sm font-semibold text-emerald-700 shadow-sm transition-all hover:border-emerald-300 hover:bg-emerald-100"
            >
              <RotateCcw className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-180" />
              Yeni hesap
            </button>

            <span className="mx-0.5 hidden h-6 w-px bg-slate-200 sm:block" aria-hidden="true" />

            <button
              type="button"
              onClick={() => dosyaInputRef.current && dosyaInputRef.current.click()}
              title=".zmn dosyasından verileri yükle"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-600 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
            >
              <Download className="h-4 w-4" /> İçe Aktar
            </button>
            <button
              type="button"
              onClick={disaAktar}
              title="Verileri .zmn dosyası olarak kaydet"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-600 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
            >
              <Upload className="h-4 w-4" /> Dışa Aktar
            </button>
          </div>
        </header>

        {/* Girdiler */}
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-5 py-4 sm:px-6">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Girdiler</p>
            <h2 className="text-base font-semibold text-slate-800">Suç ve süre bilgileri</h2>
          </div>

          <div className="space-y-6 p-5 sm:p-6">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              <TarihGirisi
                label="Suç tarihi"
                value={sucTarihi}
                onChange={setSucTarihi}
                hint="Suçun işlendiği gün (TCK 66/6)."
              />
              <ZamanAsimiSuresiSecimi value={zamanAsimiSuresi} onChange={setZamanAsimiSuresi} />
              <YasGrubuSecimi
                dogumTarihi={dogumTarihi}
                onDogumTarihiChange={setDogumTarihi}
                sucTarihi={sucTarihi}
                manuelYasGrubu={manuelYasGrubu}
                onManuelChange={setManuelYasGrubu}
                secilenYasGrubu={secilenYasGrubu}
                onYasGrubuChange={setSecilenYasGrubu}
                otomatikYasGrubu={yasBilgisi.grup}
                hata={yasBilgisi.hata}
              />
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <SebepBolumu
                baslik="Kesen sebepler"
                altBaslik="TCK 67/2 — süreyi keser, yeniden başlatır"
                accent="emerald"
                sebepler={kesenSebepler}
                bosMetin="Henüz kesen sebep eklenmedi"
                onEkle={() => setKesenModal({ acik: true, index: null })}
                onDuzenle={(i) => setKesenModal({ acik: true, index: i })}
                onSil={silKesen}
              />
              <SebepBolumu
                baslik="Durduran sebepler"
                altBaslik="TCK 67/1 — süreyi durdurur (üst sınırı yok)"
                accent="amber"
                sebepler={durduranSebepler}
                bosMetin="Henüz durduran sebep eklenmedi"
                onEkle={() => setDurduranModal({ acik: true, index: null })}
                onDuzenle={(i) => setDurduranModal({ acik: true, index: i })}
                onSil={silDurduran}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-5 py-4 sm:px-6">
            {yasBilgisi.hata ? (
              <span className="text-xs font-medium text-rose-600">{yasBilgisi.hata}</span>
            ) : null}
            <button
              type="button"
              onClick={hesapla}
              disabled={!!yasBilgisi.hata}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Calculator className="h-4 w-4" /> Hesapla
            </button>
          </div>
        </section>

        {/* Özet + Zaman çizelgesi — girdilerin hemen altında, tek kart */}
        {sonuc && sonuc.timelineModel && (
          <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <ZamanAsimiOzet sonuc={sonuc} />
            <div className="mt-6">
              <ZamanAsimiTimeline timelineModel={sonuc.timelineModel} sonuc={sonuc} />
            </div>
          </section>
        )}

        {/* Rapor */}
        {sonuc && sonuc.timelineModel && (
          <div className="mt-6 flex flex-col gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm sm:flex-row sm:items-start sm:justify-between sm:px-6">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Rapor</p>
              <h2 className="text-base font-semibold text-slate-800">Yatay A4 · siyah-beyaz baskıya uygun</h2>
              <p className="mt-0.5 text-xs text-slate-500">İndirilecek bölümleri seçin.</p>
            </div>
            <RaporIndirButonlari sonuc={sonuc} />
          </div>
        )}

        {/* Ayrıntılar — en altta */}
        <SonucGosterimi sonuc={sonuc} />
      </div>

      {/* Modaller */}
      <KesenSebepModal
        acik={kesenModal.acik}
        kapat={() => setKesenModal({ acik: false, index: null })}
        kaydet={kesenKaydet}
        mevcut={kesenModal.index != null ? kesenSebepler[kesenModal.index] : null}
      />
      <DurduranSebepModal
        acik={durduranModal.acik}
        kapat={() => setDurduranModal({ acik: false, index: null })}
        kaydet={durduranKaydet}
        mevcut={durduranModal.index != null ? durduranSebepler[durduranModal.index] : null}
        cocuk={sucaSuruklenenCocuk}
      />
    </div>
  );
}

export default ZamanAsimi;
